<template>
  <div v-if="user">
    <base-page-heading
      :title="user.name"
      show-back-button
    >
      <template #secondary-title>
        <sfs-active-badge :active="!user.deleted_at" />
      </template>
      <template #extra>
        <b-dropdown
          v-if="$can('update', user)"
          variant="primary"
          split
          @click="$bvModal.show('user-edit-modal')"
          no-flip
          right
          v-b-tooltip.hover
          title="Edit the User"
        >
          <template #button-content>
            <i class="fa fa-pencil-alt mr-md-1" />
            <span class="d-none d-md-inline">Edit</span>
          </template>
          <b-dropdown-item
            v-b-modal.user-settings-modal
          >
            <i class="fa fa-pencil-alt mr-1" />
            Edit Settings
          </b-dropdown-item>
        </b-dropdown>

        <b-dropdown
          variant="outline-secondary"
          no-caret
          right
        >
          <template #button-content>
            <i class="fa fa-ellipsis-v" />
          </template>

          <div class="d-md-none">
            <b-dropdown-item
              v-if="$can('update', user)"
              v-b-modal.user-edit-modal
            >
              <i class="fa fa-pencil-alt mr-1" /> Edit
            </b-dropdown-item>

            <b-dropdown-divider />
          </div>

          <b-dropdown-item @click="goToWorkOrders">
            <i class="fa fa-list-ul mr-1" /> View Work Orders
          </b-dropdown-item>

          <b-dropdown-item
            @click="sendWelcomeEmail"
          >
            <i class="fas fa-envelope-circle-check mr-1" />
            Send Welcome Email
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('read', 'User')"
            @click="emailAndroidCode"
          >
            <i class="fab fa-android mr-1" />
            Email Android App Invite
          </b-dropdown-item>

          <b-dropdown-divider />

          <b-dropdown-item
            v-if="$can('ios_code', 'User')"
            @click="emailIosCode"
          >
            <i class="fab fa-apple mr-1" />
            Email Technician iOS Invite
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('ios_code', 'User')"
            @click="generateIosCode"
          >
            <i class="fa fa-sync-alt mr-1" />
            {{ user.ios_code ? "Regenerate" : "Generate" }} Technician iOS Code
          </b-dropdown-item>

          <b-dropdown-divider />

          <b-dropdown-item
            v-if="$can('update', user)"
            @click="toggleActive"
          >
            <i
              v-if="!user.deleted_at"
              class="fa fa-trash mr-1"
            />
            <i
              v-else
              class="fa fa-undo mr-1"
            />
            {{ toggleActiveTooltipText }}
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('reset_mfa', 'User')"
            @click="resetMfa"
          >
            <i class="fa fa-undo mr-1" />
            Disable Multi-Factor Authentication
          </b-dropdown-item>

          <b-dropdown-divider />

          <b-dropdown-item v-b-modal.communications-list-modal>
            <i class="fa fa-history" />
            Email History
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('read', 'Version')"
            :to="{ name: 'versions', query: { model_id: user.id, model: 'User' } }"
          >
            <i class="fa fa-glasses mr-1" />
            Audit Log
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </base-page-heading>
    <div class="content pt-lg-5">
      <b-row class="mb-3">
        <b-col
          md="6"
        >
          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Role
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ user.category | humanizedFormat | capitalizeFormat }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Permissions
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <div
                    v-for="(role, index) in user.roles"
                    :key="index"
                  >
                    <b-link
                      v-b-modal="`${role.id}-user-role-info`"
                      v-b-tooltip.hover
                      title="View permissions"
                    >
                      {{ role.name | humanizedFormat | capitalizeFormat }}
                    </b-link>
                    <role-permissions-modal
                      :modal-id="`${role.id}-user-role-info`"
                      :role="role"
                    />
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Manager
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="user.manager">
                  <b-link :to="{ name: 'user', params: { id: user.manager.id } }">
                    {{ user.manager.name }}
                  </b-link>
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Services Provided
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="servicesProvided">
                  {{ servicesProvided }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Email
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :href="`mailto:${user.email}`">
                    {{ user.email }}
                  </b-link>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Phone
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :href="`tel:${user.phone}`">
                    {{ user.phone }}
                  </b-link>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row v-if="hasAnyAddressFields">
            <b-col>
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Address
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{
                    {
                      address: user.address,
                      city: user.city,
                      state: user.state,
                      zip_code: user.zip_code,
                    } | locationFormat
                  }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Time Zone
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ userTimezoneFormat(user.time_zone) }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Language Preference
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ user.locale }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>

        <b-col md="6">
          <hr class="mt-0 mb-3 d-md-none">

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  iOS Technician App Code
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <sfs-copy-button
                    v-if="user.ios_code"
                    :label="user.ios_code.code"
                    :content="user.ios_code.url"
                  />
                  <b-button
                    v-else
                    variant="outline-secondary"
                    size="sm"
                    @click="generateIosCode"
                  >
                    Generate Code
                  </b-button>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
            >
              <b-tabs content-class="pt-3">
                <b-tab title="Mobile Devices">
                  <div v-if="sortedMobileDevices.length">
                    <div
                      v-for="device in visibleMobileDevices"
                      :key="device.deviceName + device.last_seen_at"
                      class="mb-2"
                    >
                      <b-row>
                        <b-col
                          cols="auto"
                          class="mr-auto font-w600"
                        >
                          {{ device.deviceName }}
                        </b-col>
                        <b-col class="text-right">
                          {{ device.os }} {{ device.os_version }}
                        </b-col>
                      </b-row>
                      <b-row
                        v-if="device.last_seen_at"
                        class="font-size-sm text-secondary"
                      >
                        <b-col>
                          Last seen {{ device.last_seen_at | dateTimeFormat }}
                        </b-col>
                      </b-row>
                    </div>

                    <div
                      v-if="sortedMobileDevices.length > 5"
                      class="mt-3"
                    >
                      <b-link @click="showAllDevices = !showAllDevices">
                        {{ showAllDevices ? 'Show Less' : 'Show More' }}
                      </b-link>
                    </div>
                  </div>
                  <div v-else>
                    <b-row>
                      <b-col
                        cols="auto"
                        class="mr-auto"
                      >
                        -
                      </b-col>
                    </b-row>
                  </div>
                </b-tab>
                <b-tab title="App Versions">
                  <div v-if="sortedMobileVersions.length">
                    <b-row
                      v-for="entry in visibleMobileVersions"
                      :key="entry.version + entry.lastSeen"
                      class="mb-2"
                    >
                      <b-col
                        cols="auto"
                        class="mr-auto font-w600"
                      >
                        {{ entry.version }}
                      </b-col>
                      <b-col class="text-right text-secondary">
                        {{ entry.lastSeen | dateTimeFormat }}
                      </b-col>
                    </b-row>

                    <div
                      v-if="sortedMobileVersions.length > 5"
                      class="mt-3"
                    >
                      <b-link @click="showAllAppVersions = !showAllAppVersions">
                        {{ showAllAppVersions ? 'Show Less' : 'Show More' }}
                      </b-link>
                    </div>
                  </div>
                  <div v-else>
                    <b-row>
                      <b-col
                        cols="auto"
                        class="mr-auto"
                      >
                        -
                      </b-col>
                    </b-row>
                  </div>
                </b-tab>
              </b-tabs>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row v-if="showBottomTabs">
        <b-col cols="12">
          <b-tabs
            content-class="pt-4 mb-3"
            v-model="activeTab"
          >
            <template #tabs-end>
              <div
                v-show="!isEmployee && activeTab === 0"
                class="ml-auto"
              >
                <b-button
                  v-if="$can('create', 'UserOwnership')"
                  v-b-modal.user-ownerships-add-modal
                  variant="outline-secondary"
                  v-b-tooltip.hover
                  title="Create a new Sub Technician for this User"
                >
                  <i class="fa fa-plus mr-1" /> New
                </b-button>
              </div>

              <div
                v-show="(isEmployee && activeTab === 0) || (!isEmployee && activeTab === 1)"
                class="ml-auto"
              >
                <b-button
                  v-if="$can('create', 'RecurringPayment')"
                  v-b-modal.recurring-payment-add-modal
                  variant="outline-secondary"
                  v-b-tooltip.hover
                  title="Create a new Recurring Payment for this User"
                >
                  <i class="fa fa-plus mr-1" /> New Recurring Payment
                </b-button>
              </div>

              <div
                v-show="activeTab === notesTabIndex"
                class="ml-auto"
              >
                <b-button
                  variant="outline-secondary"
                  @click="$refs.notesTimeline && $refs.notesTimeline.toggleTimelineForm()"
                >
                  <i class="fa fa-plus mr-1" /> New Note
                </b-button>
              </div>
            </template>

            <b-tab
              v-if="!isEmployee"
              :active="!isEmployee"
              :title="userOwnershipsHeader"
            >
              <user-ownerships-table
                :user="user"
                :append-to-query="false"
                :key="userOwnershipsTableKey"
              />
            </b-tab>

            <b-tab
              v-if="$can('read', 'RecurringPayment')"
              :active="isEmployee"
              title="Recurring Payments"
            >
              <recurring-payments-table
                :columns="recurringPaymentsColumns"
                :user-id="Number(user.id)"
                :append-to-query="false"
                :key="recurringPaymentsTableKey"
              />
            </b-tab>

            <b-tab
              v-if="$can('read', 'ComplianceDocument')"
              title="Compliance Documents"
            >
              <compliance-documents-table
                :columns="complianceDocumentColumns"
                :filters="complianceDocumentFilters"
                :append-to-query="false"
                :key="complianceDocumentTableKey"
              />
            </b-tab>

            <b-tab>
              <template #title>
                <number-badge
                  title="Notes"
                  :total="notesTotal"
                />
              </template>

              <notes-timeline
                ref="notesTimeline"
                :list-func="$notesAPI.getUsersNotes"
                :create-func="createUserNoteAdapter"
                :reference-id="user.id"
                reference-type="User"
                hide-new-note-button
                @total-rows-changed="(newTotal) => notesTotal = newTotal"
              />
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </div>

    <!-- Modals -->
    <edit-modal
      v-if="$can('update', user)"
      v-model="user"
    />
    <settings-modal
      v-if="$can('update', user)"
      v-model="user"
    />
    <new-recurring-payment-modal
      v-if="$can('create', 'RecurringPayment')"
      :user-id="user.id"
      @on-save="refreshRecurringPayments"
      :key="user.id"
    />

    <!-- Modals -->
    <new-user-ownerships-modal
      v-if="$can('create', 'UserOwnership')"
      :technician_id="technicianId"
      :technician="technician"
      :sub_technician_id="subTechnicianId"
      :sub_technician="subTechnician"
      :is-sub-technician="isSubTechnician"
      @save="refreshUserOwnerships"
    />
    <communications-list-modal
      :list-provider="$communicationsAPI.getUserCommunications"
      :reference-id="user.id"
      title="Email History"
    />
  </div>
</template>

<script>
import EditModal from '@/components/admin/users/EditModal.vue';
import SettingsModal from '@/components/admin/users/SettingsModal.vue';
import UserOwnershipsTable from '@/components/admin/userOwnerships/Table.vue';
import RecurringPaymentsTable from '@/components/admin/recurringPayments/Table.vue';
import NewRecurringPaymentModal from '@/components/admin/recurringPayments/NewModal.vue';
import NewUserOwnershipsModal from '@/components/admin/userOwnerships/NewModal.vue';
import RolePermissionsModal from '@/components/shared/RolePermissionsModal.vue';
import CommunicationsListModal from '@/components/admin/communications/ListModal.vue';
import ComplianceDocumentsTable from '@/components/admin/complianceDocuments/Table.vue';
import NotesTimeline from '@/components/admin/notes/Timeline.vue';
import NumberBadge from '@/components/shared/NumberBadge.vue';
import { USER_TECHNICIAN_CATEGORY, USER_SUB_TECHNICIAN_CATEGORY } from '@/constants/users';
import { RECURRING_PAYMENT_TABLE_NAME } from '@/constants/recurringPayments';
import { COMPLIANCE_DOCUMENT_TABLE_COLUMNS } from '@/constants/complianceDocuments';
import formMixin from '@/mixins/formMixin';
import errorToastMixin from '@/mixins/errorToastMixin';
import { DateTime } from 'luxon';

export default {
  name: 'UsersShow',
  mixins: [formMixin, errorToastMixin],
  components: {
    EditModal,
    UserOwnershipsTable,
    NewUserOwnershipsModal,
    RecurringPaymentsTable,
    NewRecurringPaymentModal,
    RolePermissionsModal,
    SettingsModal,
    CommunicationsListModal,
    ComplianceDocumentsTable,
    NotesTimeline,
    NumberBadge,
  },
  data() {
    return {
      user: null,
      notesTotal: 0,
      activeTab: 0,
      recurringPaymentsTableName: RECURRING_PAYMENT_TABLE_NAME,
      userOwnershipsKey: Date.now(),
      recurringPaymentsKey: Date.now() + 1,
      timezoneOptions: [],
      showAllDevices: false,
      showAllAppVersions: false,
      genericIosErrorMessage: 'We\'ve experienced an issue while trying to generate the code, Please try again.',
    };
  },
  computed: {
    hasAnyAddressFields() {
      return (
        (this.user.address || '').length > 0 ||
        (this.user.city || '').length > 0 ||
        (this.user.state || '').length > 0 ||
        (this.user.zip_code || '').length > 0
      );
    },
    toggleActiveTooltipText() {
      return !this.user.deleted_at ? 'Deactivate the User' : 'Reactivate the User';
    },
    toggleActiveConfirmText() {
      return !this.user.deleted_at
        ? 'Are you sure you want to cancel the User?'
        : 'Are you sure you want to reactivate the User?';
    },
    isTechnician() {
      return this.user.category == USER_TECHNICIAN_CATEGORY;
    },
    isSubTechnician() {
      return this.user.category == USER_SUB_TECHNICIAN_CATEGORY;
    },
    isEmployee() {
      return !this.isTechnician && !this.isSubTechnician;
    },
    technician() {
      return this.isSubTechnician ? undefined : this.user;
    },
    technicianId() {
      return this.isSubTechnician ? undefined : this.user.id;
    },
    subTechnician() {
      return this.isSubTechnician ? this.user : undefined;
    },
    subTechnicianId() {
      return this.isSubTechnician ? this.user.id : undefined;
    },
    servicesProvided() {
      if (this.user.services_provided.length === 0) {
        return null;
      }

      return this.user.services_provided.map((service) => this.$options.filters.capitalizeFormat(this.$options.filters.humanizedFormat(service))).join(', ');
    },
    userOwnershipsTableKey() {
      return `${this.user.id}-${this.userOwnershipsKey}`;
    },
    recurringPaymentsTableKey() {
      return `${this.user.id}-${this.recurringPaymentsKey}`;
    },
    recurringPaymentsColumns() {
      return this.$store.getters.columnsForTable(this.recurringPaymentsTableName);
    },
    complianceDocumentColumns() {
      return [
        COMPLIANCE_DOCUMENT_TABLE_COLUMNS.number,
        COMPLIANCE_DOCUMENT_TABLE_COLUMNS.status,
        COMPLIANCE_DOCUMENT_TABLE_COLUMNS.state,
        COMPLIANCE_DOCUMENT_TABLE_COLUMNS.insurance_contact,
        COMPLIANCE_DOCUMENT_TABLE_COLUMNS.end_date,
      ];
    },
    complianceDocumentFilters() {
      if (!this.user) {
        return {};
      }

      return { user_id: this.user.id };
    },
    complianceDocumentTableKey() {
      return this.user ? `${this.user.id}-compliance-documents` : 'compliance-documents';
    },
    showBottomTabs() {
      return (
        this.$can('read', 'RecurringPayment') ||
        this.$can('read', 'ComplianceDocument') ||
        this.isTechnician ||
        this.isSubTechnician
      );
    },
    userOwnershipsHeader() {
      return this.isSubTechnician ? 'Managing Technicians' : 'Sub Technicians';
    },
    // Notes is always the last tab; its index shifts with the conditionally-rendered tabs before it.
    notesTabIndex() {
      return (this.isEmployee ? 0 : 1)
        + (this.$can('read', 'RecurringPayment') ? 1 : 0)
        + (this.$can('read', 'ComplianceDocument') ? 1 : 0);
    },
    sortedMobileDevices() {
      if (!this.user || !this.user.devices) {
        return [];
      }

      return Object.entries(this.user.devices)
        .map(([deviceName, data]) => ({ deviceName, ...data }))
        .sort((a, b) => {
          return new Date(b.last_seen_at) - new Date(a.last_seen_at);
        });
    },
    visibleMobileDevices() {
      const devices = this.sortedMobileDevices;
      return this.showAllDevices ? devices : devices.slice(0, 5);
    },
    sortedMobileVersions() {
      if (!this.user || !this.user.app_versions) {
        return [];
      }

      return Object.entries(this.user.app_versions)
        .map(([version, lastSeen]) => ({ version, lastSeen }))
        .sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen));
    },
    visibleMobileVersions() {
      const versions = this.sortedMobileVersions;
      return this.showAllAppVersions ? versions : versions.slice(0, 5);
    },
  },
  mounted() {
    this.getUser();
    this.getTimeZoneOptions();
  },
  watch: {
    $route: 'getUser',
  },
  methods: {
    createUserNoteAdapter(referenceId, data) {
      return this.$notesAPI.createUsersNote(referenceId, { ...data, reference_type: 'User', reference_id: referenceId });
    },
    getUser() {
      this.$usersAPI
        .get(this.$route.params.id)
        .then((user) => (this.user = user))
        .catch(() => {
          this.$router.push('/404');
        });
    },
    getTimeZoneOptions() {
      this.$usersAPI.getTimeZoneOptions().then((response) => {
        this.timezoneOptions = response.data.time_zone_options.map((option) => {
          return { name: option.name, value: option.identifier };
        });
      });
    },
    userTimezoneFormat(timezone) {
      for (let i = 0; i < this.timezoneOptions.length; i++) {
        if(timezone === this.timezoneOptions[i].value) {
          timezone = this.timezoneOptions[i].name;
        }
      }
      return timezone;
    },
    refreshUserOwnerships() {
      this.userOwnershipsKey = Date.now();
    },
    refreshRecurringPayments() {
      this.recurringPaymentsKey = Date.now() + 1;
    },
    toggleActive() {
      this.$swal({
        text: this.toggleActiveConfirmText,
        preConfirm: () => this.$usersAPI.update(this.$route.params.id, { deleted_at: !this.user.deleted_at ?  DateTime.now().toISODate() : null }),
      }).then((result) => {
        if (result.value) {
          this.user = result.value;
        }
        result.timer;
      });
    },
    generateIosCode() {
      this.$usersAPI
        .generateIosCode(this.$route.params.id, 'technician')
        .then((user) => (this.user = user))
        .catch((error) => {
          if (error.response && error.response.status === 422 && error.response.data.errors) {
            this.processServerErrors(error.response.data.errors);
            this.toastDanger(this.hasBaseServerErrors() ? this.baseServerErrors : this.genericIosErrorMessage);
          } else {
            this.toastDanger(this.genericIosErrorMessage);
          }
        });
    },
    resetMfa() {
      this.$swal({
        text: 'Do you want to disable Multi-Factor Authentication?',
        preConfirm: () => {
          return this.$usersAPI.resetMfa(this.$route.params.id).catch((error) => this.displayErrorToast(error, { fallbackMessage: this.genericIosErrorMessage }));
        },
      }).then((result) => {
        if (result.value) {
          this.user = result.value;
        }
        result.timer;
      });
    },
    emailAndroidCode() {
      this.$swal({
        text: 'Do you want to email the user an Android App invite?',
        preConfirm: () => {
          return this.$usersAPI.emailAndroidCode(this.$route.params.id).catch((error) => this.displayErrorToast(error, { fallbackMessage: this.genericIosErrorMessage }));
        },
      }).then((result) => {
        if (result.value) {
          this.user = result.value;
        }
        result.timer;
      });
    },
    emailIosCode() {
      this.$swal({
        text: 'Do you want to email the user an iOS app invite?',
        preConfirm: () => {
          return this.$usersAPI.emailIosCode(this.$route.params.id, 'technician').catch((error) => this.displayErrorToast(error, { fallbackMessage: this.genericIosErrorMessage }));
        },
      }).then((result) => {
        if (result.value) {
          this.user = result.value;
        }
        result.timer;
      });
    },
    goToWorkOrders() {
      if (!this.user) { return; }
      this.$router.push({ name: 'work-orders', query: { [this.user.category === 'sub_technician' ? 'done_by_id' : 'assigned_to_id']: String(this.user.id) } });
    },
    sendWelcomeEmail() {
      this.$swal({
        text: 'Do you want to send a welcome email to the User?',
        preConfirm: () => {
          return this.$usersAPI.sendWelcomeEmail(this.$route.params.id).catch((error) => this.displayErrorToast(error, { fallbackMessage: this.genericIosErrorMessage }));
        },
      }).then((result) => {
        if (result.value) {
          this.user = result.value;
        }
        result.timer;
      });
    },
  },
};
</script>
